import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InsuranceApiService, InsurancePremiumInvoiceListItem, InsuranceProducerListItem, InsuranceProductListItem } from './insurance-api.service';

@Component({
  standalone: true,
  selector: 'ain-insurance-premium-invoices',
  imports: [
    AinAmountPipe,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    PartySelectorComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule
  ],
  template: `
    <ain-page
      [title]="t('insurance.premiums.invoices.title')"
      [description]="t('insurance.premiums.invoices.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.premiums.help.page.title'"
        [descriptionKey]="'insurance.premiums.help.page.description'"
        [bulletKeys]="[
          'insurance.premiums.help.page.bullets.scope',
          'insurance.premiums.help.page.bullets.receivables',
          'insurance.premiums.help.page.bullets.selection'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('insurance.states.noPermission')" [message]="t('insurance.states.noPermissionMessage')" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.open')" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('insurance.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'insurance.premiums.help.listFilters.title'"
                [descriptionKey]="'insurance.premiums.help.listFilters.description'"
                [bulletKeys]="[
                  'insurance.premiums.help.listFilters.bullets.producer',
                  'insurance.premiums.help.listFilters.bullets.lineOfBusiness',
                  'insurance.premiums.help.listFilters.bullets.status'
                ]" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('insurance.premiums.invoices.filters.producer') }}</span>
                <ain-party-selector [(ngModel)]="producerId" name="producerId" />
              </label>
              <label>
                <span>{{ t('insurance.premiums.invoices.filters.lineOfBusiness') }}</span>
                <p-select
                  [options]="lineOfBusinessOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [(ngModel)]="lineOfBusiness"
                  name="lineOfBusiness"
                  [showClear]="true"
                  [filter]="true"
                  appendTo="body" />
              </label>
              <label>
                <span>{{ t('insurance.premiums.invoices.filters.status') }}</span>
                <p-select
                  [options]="invoiceStatusOptions"
                  optionValue="value"
                  [(ngModel)]="statusFilter"
                  name="statusFilter"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              <div class="actions">
                <button pButton type="button" [label]="t('common.apply')" (click)="load()"></button>
              </div>
            </div>
          </details>

          <div class="section-title">
            <h3>{{ t('insurance.premiums.receivables.title') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.premiums.help.receivables.title'"
              [descriptionKey]="'insurance.premiums.help.receivables.description'"
              [bulletKeys]="[
                'insurance.premiums.help.receivables.bullets.grouping',
                'insurance.premiums.help.receivables.bullets.scope'
              ]" />
          </div>
          @if (receivableSummary().length === 0) {
            <p>{{ t('insurance.premiums.receivables.empty') }}</p>
          } @else {
            <div class="summary-grid">
              @for (item of receivableSummary(); track item.key) {
                <div class="summary-item">
                  <span>{{ producerLabel(item.producerId) }} / {{ lobLabel(item.lineOfBusiness) }}</span>
                  <strong>{{ item.outstanding | ainAmount }}</strong>
                </div>
              }
            </div>
          }

          @if (rows().length === 0) {
            <ain-state type="empty" [title]="t('insurance.premiums.invoices.states.empty')" [message]="t('insurance.premiums.invoices.states.emptyMessage')" />
          } @else {
            <p-table
              [value]="rows()"
              dataKey="id"
              [paginator]="true"
              [rows]="10"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th>{{ t('insurance.premiums.invoices.fields.number') }}</th>
                  <th>{{ t('insurance.premiums.invoices.fields.policy') }}</th>
                  <th>{{ t('insurance.premiums.invoices.fields.lineOfBusiness') }}</th>
                  <th>{{ t('insurance.premiums.invoices.fields.amount') }}</th>
                  <th>{{ t('insurance.premiums.invoices.fields.collected') }}</th>
                  <th>{{ t('insurance.premiums.invoices.fields.outstanding') }}</th>
                  <th>{{ t('columns.status') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.invoiceNumber }}</button></td>
                  <td>{{ row.policyNumber }}</td>
                  <td>{{ lobLabel(row.lineOfBusiness) }}</td>
                  <td>{{ row.amount + row.taxAmount | ainAmount }}</td>
                  <td>{{ row.collectedAmount | ainAmount }}</td>
                  <td>{{ row.outstandingAmount | ainAmount }}</td>
                  <td>{{ t(statusKey(row.status)) }}</td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; }
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .filters { border: 1px solid #e5e7eb; border-radius: 10px; padding: 8px 10px; background: #f9fafb; }
    .filters summary { display: flex; align-items: center; gap: 8px; }
    .filter-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; margin-top: 8px; }
    .filter-grid label { display: grid; gap: 6px; }
    .actions { display: flex; align-items: end; }
    .section-title { display: flex; align-items: center; gap: 8px; margin-top: 4px; }
    .section-title h3 { margin: 0; font-size: 16px; }
    .summary-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
    .summary-item { border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px; display: flex; justify-content: space-between; gap: 8px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) {
      .filter-grid, .summary-grid { grid-template-columns: 1fr; }
    }
  `]
})
export class InsurancePremiumInvoicesComponent implements OnInit {
  private readonly api = inject(InsuranceApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly error = signal('');
  readonly permissionDenied = signal(false);
  readonly rows = signal<InsurancePremiumInvoiceListItem[]>([]);
  readonly selectedRows = signal<InsurancePremiumInvoiceListItem[]>([]);
  readonly products = signal<InsuranceProductListItem[]>([]);
  readonly producers = signal<InsuranceProducerListItem[]>([]);
  producerId = '';
  lineOfBusiness: string | null = null;
  statusFilter: string | null = null;

  readonly invoiceStatusOptions = [
    { value: 'Open', labelKey: 'insurance.premiums.status.open' },
    { value: 'PartiallyPaid', labelKey: 'insurance.premiums.status.partiallyPaid' },
    { value: 'Paid', labelKey: 'insurance.premiums.status.paid' },
    { value: 'WrittenOff', labelKey: 'insurance.premiums.status.writtenOff' },
    { value: 'Cancelled', labelKey: 'insurance.premiums.status.cancelled' }
  ];

  readonly receivableSummary = computed(() => {
    const groups = new Map<string, { key: string; producerId: string | null; lineOfBusiness: string; outstanding: number }>();
    for (const row of this.rows()) {
      const key = `${row.producerId ?? 'none'}|${row.lineOfBusiness}`;
      const item = groups.get(key) ?? {
        key,
        producerId: row.producerId,
        lineOfBusiness: row.lineOfBusiness,
        outstanding: 0
      };
      item.outstanding += row.outstandingAmount;
      groups.set(key, item);
    }
    return [...groups.values()].sort((a, b) => b.outstanding - a.outstanding);
  });

  ngOnInit(): void {
    this.api.listProducts().subscribe({
      next: rows => this.products.set(rows),
      error: () => this.products.set([])
    });
    this.api.listProducers().subscribe({
      next: rows => this.producers.set(rows),
      error: () => this.producers.set([])
    });
    this.load();
  }

  producerLabel(producerId: string | null): string {
    if (!producerId) {
      return '-';
    }

    const producer = this.producers().find(item => item.id === producerId);
    return producer ? `${producer.code} - ${producer.name}` : this.t('insurance.premiums.invoices.filters.producer');
  }

  lobLabel(value: string): string {
    const key = value.trim().toLowerCase();
    const translationKey = key === 'motor' || key === 'motor-tp'
      ? 'insurance.lineOfBusiness.motor'
      : key === 'liability' || key === 'construction-el'
        ? 'insurance.lineOfBusiness.liability'
        : '';
    return translationKey ? this.t(translationKey) : value;
  }

  lineOfBusinessOptions(): Array<{ label: string; value: string }> {
    const values = new Set<string>();
    for (const product of this.products()) {
      const value = (product.lineOfBusiness ?? '').trim();
      if (value) {
        values.add(value);
      }
    }
    return [...values].sort().map(value => ({ value, label: value }));
  }

  load(): void {
    this.loading.set(true);
    this.api.listPremiumInvoices({
      producerId: this.producerId || null,
      lineOfBusiness: this.lineOfBusiness || null,
      status: this.statusFilter || null
    }).subscribe({
      next: rows => {
        this.rows.set(rows);
        this.error.set('');
        this.permissionDenied.set(false);
        this.loading.set(false);
      },
      error: err => {
        this.rows.set([]);
        this.permissionDenied.set(err?.status === 403);
        this.error.set(err?.status === 403 ? '' : this.t('insurance.states.loadError'));
        this.loading.set(false);
      }
    });
  }

  onSelectionChange(selection: InsurancePremiumInvoiceListItem[] | InsurancePremiumInvoiceListItem | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  openSelected(): void {
    const row = this.selectedRows()[0];
    if (row) {
      this.openDetail(row.id);
    }
  }

  openDetail(id: string): void {
    void this.router.navigate(['/insurance/premium-invoices', id]);
  }

  statusKey(status: InsurancePremiumInvoiceListItem['status']): string {
    return `insurance.premiums.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
